import { nanoid } from 'nanoid';
import { v4 as uuidv4 } from 'uuid';

const sponsorshipConstants = Object.freeze({
  initialState: {
    id: uuidv4(),
    name: 'New Ad',
    isDirty: false,
    isCreated: false,
    isDraft: true,
    isActive: false,
    isReadOnly: false,
    moderationStatus: 'Unknown',
    deliveryStatus: 'Inactive',
    smsMessage: '',
    processingAdCover: '',
    processingAdCoverFile: {},
    thirdPartyTrackingScripts: [],
    adCover: '',
    adCoverFile: {},
    adVideo: '',
    adVideoFile: {},
    videoLength: '0:00:00',
    isVideoUploaded: false,
    progress: 0,
    videoUploadStatus: '',
    videoKey: '',
    processingCtaCover: '',
    processingCtaCoverFile: {},
    ctaCover: '',
    ctaCoverFile: {},
    ctaLink: '',
    ctaButtonText: '',
    ctaDiscount: '',
    trackingUrl: '',
    receivedQuestions: false,
    appToTrack: { name: 'Any', label: 'Any', value: 'any' },
    questions: [
      {
        id: nanoid(),
        name: 'Question',
        saved: false,
        text: '',
        questionOrder: 1,
        type: { label: 'Yes/No', name: 'yesno', value: 'YesNo' },
        options: [],
        responsesOptions: [
          {
            id: nanoid(),
            name: 'Yes',
            text: 'Yes',
            tags: [],
          },
          {
            id: nanoid(),
            name: 'No',
            text: 'No',
            tags: [],
          },
        ],
        tagsCount: 0,
      },
    ],
    questionToAddTags: '',
    localTags: [],
    impressionsUrl: '',
    completionUrl: '',
    internalFetching: false,
    videoWidth: '',
    videoHeight: '',
    previousSponsorshipNames: [],
  },
  adCoverFileSpecs: [
    {
      name: 'File Type',
      values: '.jpg, .png',
      icon: 'file-type',
    },
    {
      name: 'Aspect Ratio',
      values: '16:9, 1:1, 4:3',
      icon: 'aspect-ratio',
    },
  ],

  videoAdFileSpecs: [
    {
      name: 'File Type',
      values: '.Mp4, .MOV',
      icon: 'file-type',
    },
    {
      name: 'Resolution',
      values: 'minimum width: 600p',
      icon: 'resolution',
    },
    {
      name: 'Aspect Ratio',
      values: '9:16, 1:1, 4:3, 16:9, 3:4',
      icon: 'aspect-ratio',
    },
  ],

  ctaImageFileSpecs: [
    {
      name: 'File Type',
      values: '.jpg, .png',
      icon: 'file-type',
    },
    {
      name: 'Resolution',
      values: 'minimum width: 600p',
      icon: 'resolution',
    },
    {
      name: 'Aspect Ratio',
      values: '1:1',
      icon: 'aspect-ratio',
    },
  ],
  defaultOption: {
    id: nanoid(),
    name: 'Option',
    text: '',
    tags: [],
  },
  defaultQuestion: {
    id: nanoid(),
    name: 'Question',
    text: '',
    saved: false,
    questionOrder: 1,
    type: { label: 'Yes/No', name: 'yesno', value: 'YesNo' },
    options: [],
    responsesOptions: [
      {
        id: nanoid(),
        name: 'Yes',
        text: 'Yes',
        tags: [],
        tagsOptions: [],
      },
      {
        id: nanoid(),
        name: 'No',
        text: 'No',
        tags: [],
        tagsOptions: [],
      },
    ],
    tagsCount: 0,
  },
  defaultQuestions: [
    {
      id: nanoid(),
      name: 'Question',
      saved: false,
      text: '',
      questionOrder: 1,
      type: { label: 'Yes/No', name: 'yesno', value: 'YesNo' },
      options: [],
      responsesOptions: [
        {
          id: nanoid(),
          name: 'Yes',
          text: 'Yes',
          tags: [],
        },
        {
          id: nanoid(),
          name: 'No',
          text: 'No',
          tags: [],
        },
      ],
    },
  ],

  questionsTypes: [
    { label: 'Yes/No', name: 'yesno', value: 'YesNo' },
    { label: 'Viewer Sentiment', name: 'viewersentiment', value: 'ViewerSentiment' },
    { label: 'Multiple Choice', name: 'multiplechoice', value: 'MultipleChoice' },
    { label: 'Select All That Apply', name: 'selectallthatapply', value: 'SelectAllThatApply' },
    { label: 'Short Answer', name: 'shortanswer', value: 'ShortAnswer' },
  ],

  addToTrackOptions: [
    { label: 'Any', name: 'Any', value: 'any' },
    { label: 'iOS', name: 'iOS', value: 'iOS' },
    { label: 'Android', name: 'Android', value: 'Android' },
  ],

  conversionTracking: [
    {
      sectionName: 'Tracking pixel status',
      iconName: 'status-warning',
      text: 'No tracking pixel',
      linkClass: 'link-red',
      linkText: 'Set up tracking pixel',
      linkUrl: 'https://www.weare8.com/',
    },
    {
      sectionName: 'Tracking pixel status',
      iconName: 'status-ok',
      text: 'Tracking pixel active',
      linkClass: 'link-violet',
      linkText: 'See tracking pixel',
      linkUrl: 'https://www.weare8.com/',
    },
    {
      sectionName: 'Tracking pixel status',
      iconName: 'status-warning',
      text: 'No tracking pixel',
      linkClass: 'link-red',
      linkText: 'Test tracking pixel',
      linkUrl: 'https://www.weare8.com/',
    },
  ],
  viewerSentimentAnswers: ['0x1F620', '0x1F641', '0x1F610', '0x1F642', '0x1F601'],
  yesNoTagsOptions: [
    {
      id: nanoid(),
      name: 'Yes',
      text: 'Yes',
      tags: [],
    },
    {
      id: nanoid(),
      name: 'No',
      text: 'No',
      tags: [],
    },
  ],
  viewerSentimentTagsOptions: [
    {
      id: nanoid(),
      name: '1',
      text: '0x1F620',
      tags: [],
    },
    {
      id: nanoid(),
      name: '2',
      text: '0x1F641',
      tags: [],
    },
    {
      id: nanoid(),
      name: '3',
      text: '0x1F610',
      tags: [],
    },
    {
      id: nanoid(),
      name: '4',
      text: '0x1F642',
      tags: [],
    },
    {
      id: nanoid(),
      name: '5',
      text: '0x1F601',
      tags: [],
    },
  ],
  fieldNames: [
    {
      value: 'name',
      label: 'Ad Name',
    },
    {
      value: 'adVideo',
      label: 'Ad Video',
    },
    {
      value: 'questions',
      label: 'Questions',
    },
    {
      value: 'ctaCover',
      label: 'Call To Action Image',
    },
    {
      value: 'ctaLink',
      label: 'Call To Action Link',
    },
    {
      value: 'ctaButtonText',
      label: 'Custom CTA button text',
    },
    {
      value: 'ctaDiscount',
      label: 'Discount Code',
    },
    {
      value: 'ctaTrackingService',
      label: 'Third Party Tracking Service',
    },
    {
      value: 'ctaTrackingScript',
      label: 'Third Party Tracking Code',
    },
  ],
  thirdPartyScriptOptions: [
    { name: 'DoubleClick', value: 'doubleClick' },
    { name: 'DoubleVerify', value: 'doubleVerify' },
  ],
});

export default sponsorshipConstants;
